import {useCallback, useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import * as ViewHooks from '../../../state/views/hooks';
import {Ref as PanelRef} from '../../../state/views/panel/types';
import {
  allowsFullScreenPanelsByQueryParams,
  isFullScreenWorkspacePanelByIdPath,
} from '../utils';

export const useConfigXBounds = (panelRef: PanelRef) => {
  const initialConfigState = ViewHooks.usePart(panelRef);

  /**
   * In a runs line plot panel we need to pass in the saved xAxisMin and xAxisMax values so that we can set the initial zoom on the queryManager, otherwise it will default to null | null for the min | max
   */
  const configXBounds = useMemo(() => {
    if (
      'xAxisMin' in initialConfigState.config &&
      'xAxisMax' in initialConfigState.config
    ) {
      return {
        xAxisMin: initialConfigState.config?.xAxisMin ?? null,
        xAxisMax: initialConfigState.config?.xAxisMax ?? null,
      };
    }
    return {};
  }, [initialConfigState.config]);

  return configXBounds;
};

export const useDetectPanelVariant = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const {panelDisplayName, panelSectionName} = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      panelDisplayName: searchParams.get('panelDisplayName'),
      panelSectionName: searchParams.get('panelSectionName'),
    };
  }, [location.search]);

  // Memoize the expensive route matching operations based on the path
  const routeMatchingResults = useMemo(() => {
    const isFullScreenPanelById =
      isFullScreenWorkspacePanelByIdPath(currentPath);
    const isPageWithPanels = allowsFullScreenPanelsByQueryParams(currentPath);
    return {
      isFullScreenPanelById,
      isPageWithPanels,
    };
  }, [currentPath]);

  // Memoize the panel variant calculations based on search params and route matching
  const value = useMemo(() => {
    const {isFullScreenPanelById, isPageWithPanels} = routeMatchingResults;

    const isFullScreenPanelByNameAndSectionName =
      panelDisplayName && panelSectionName && isPageWithPanels;

    const isFullScreenPanelByName =
      !isFullScreenPanelByNameAndSectionName &&
      panelDisplayName &&
      isPageWithPanels;

    const isFullScreenPanelPage =
      isFullScreenPanelById ||
      isFullScreenPanelByName ||
      isFullScreenPanelByNameAndSectionName;

    return {
      isFullScreenPanelById,
      isFullScreenPanelByName,
      isFullScreenPanelByNameAndSectionName,
      isFullScreenPanelPage,
    };
  }, [routeMatchingResults, panelDisplayName, panelSectionName]);

  return value;
};

// Note: we should add unit tests for these
export const useGoToBaseUrl = () => {
  const history = useHistory();
  const location = useLocation();
  const {
    isFullScreenPanelById,
    isFullScreenPanelByName,
    isFullScreenPanelByNameAndSectionName,
  } = useDetectPanelVariant();

  const goToBaseURL = useCallback(() => {
    const currentPath = location.pathname;
    const queryParamsObj = new URLSearchParams(location.search);
    // these zoom params are how zoom states in a panel are syndicated to full screen
    // if left they'll update the panels in the workspace so they need to be removed on panel close
    ['xAxisMin', 'xAxisMax', 'yAxisMin', 'yAxisMax'].forEach(param => {
      queryParamsObj.delete(param);
    });

    if (isFullScreenPanelById) {
      const panelIndex = currentPath.indexOf('/panel/');
      if (panelIndex !== -1) {
        const basePath = currentPath.substring(0, panelIndex);
        history.replace(`${basePath}?${queryParamsObj.toString()}`);
      } else {
        history.replace(`/?${queryParamsObj.toString()}`);
      }
    } else if (isFullScreenPanelByName) {
      queryParamsObj.delete('panelDisplayName');
      history.replace(
        `${window.location.pathname}?${queryParamsObj.toString()}`
      );
    } else if (isFullScreenPanelByNameAndSectionName) {
      queryParamsObj.delete('panelDisplayName');
      queryParamsObj.delete('panelSectionName');
      history.replace(
        `${window.location.pathname}?${queryParamsObj.toString()}`
      );
    }
  }, [
    isFullScreenPanelById,
    isFullScreenPanelByName,
    isFullScreenPanelByNameAndSectionName,
    history,
    location,
  ]);

  return goToBaseURL;
};
